(() => {
  const endpoint = 'https://a.klaviyo.com/client/subscriptions/';
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const defaultRevision = '2026-07-15';
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  const dispatch = (form, type, detail) => {
    form.dispatchEvent(new CustomEvent(`nh:klaviyo-subscribe:${type}`, { bubbles: true, detail }));
  };

  const getErrorDetail = (body) => {
    if (!body || !body.errors || !body.errors[0]) return '';
    return body.errors[0].detail || '';
  };

  // Deutsche Schreibweisen (0171 …, 0049 …, +49 …) nach E.164; alles andere bleibt leer.
  const toE164 = (value) => {
    let phone = (value || '').replace(/[^\d+]/g, '');
    if (phone.startsWith('00')) phone = `+${phone.slice(2)}`;
    else if (phone.startsWith('0')) phone = `+49${phone.slice(1)}`;
    return /^\+[1-9]\d{6,14}$/.test(phone) ? phone : '';
  };

  // Optionale Profilfelder aus Inputs mit data-nh-klaviyo-field (full_name, first_name, last_name, phone_number).
  const getProfileFields = (form) => {
    const fields = {};
    form.querySelectorAll('[data-nh-klaviyo-field]').forEach((input) => {
      const value = (input.value || '').trim();
      if (!value) return;
      const key = input.dataset.nhKlaviyoField;

      if (key === 'full_name') {
        const [first, ...rest] = value.split(/\s+/);
        fields.first_name = first;
        if (rest.length) fields.last_name = rest.join(' ');
      } else if (key === 'phone_number') {
        const phone = toE164(value);
        if (phone) fields.phone_number = phone;
        fields.properties = { ...fields.properties, telefon: value };
      } else {
        fields[key] = value;
      }
    });
    return fields;
  };

  const getUtm = () => {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    utmKeys.forEach((key) => {
      const value = (params.get(key) || '').trim();
      if (value) utm[key] = value.slice(0, 200);
    });
    return utm;
  };

  const subscribe = async ({ email, companyId, listId, revision = defaultRevision, profile = {} }) => {
    const attributes = { ...profile, email };

    // Mit Telefonnummer nur den E-Mail-Kanal abonnieren, sonst würde Klaviyo auch SMS eintragen.
    if (attributes.phone_number) {
      attributes.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    const response = await fetch(`${endpoint}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision
      },
      body: JSON.stringify(payload)
    });
    const body = response.status === 204 ? null : await response.json().catch(() => null);

    return {
      ok: response.ok,
      status: response.status,
      detail: getErrorDetail(body),
      body
    };
  };

  const submit = async (event) => {
    const form = event.target.closest('[data-nh-klaviyo-subscribe]');
    if (!form || form.dataset.nhKlaviyoSubmitting === 'true') return;

    event.preventDefault();

    const emailInput = form.querySelector('input[type="email"]');
    const email = (emailInput?.value || '').trim();
    const companyId = form.dataset.companyId;
    const listId = form.dataset.listId;
    const revision = form.dataset.revision || defaultRevision;

    if (!emailPattern.test(email)) {
      emailInput?.focus();
      dispatch(form, 'error', { type: 'invalid', email });
      return;
    }

    if (!companyId || !listId) {
      dispatch(form, 'error', { type: 'config', email });
      return;
    }

    form.dataset.nhKlaviyoSubmitting = 'true';
    dispatch(form, 'loading', { isLoading: true });

    try {
      const profile = getProfileFields(form);
      const utm = getUtm();
      if (Object.keys(utm).length) profile.properties = { ...profile.properties, ...utm };

      const result = await subscribe({ email, companyId, listId, revision, profile });

      if (result.ok) {
        dispatch(form, 'success', { email, status: result.status, body: result.body });
      } else {
        console.error('Klaviyo subscription request failed:', result.status, result.body);
        dispatch(form, 'error', { type: 'request', email, ...result });
      }
    } catch (error) {
      console.error('Klaviyo subscription request failed:', error);
      dispatch(form, 'error', { type: 'connection', email, error });
    } finally {
      form.dataset.nhKlaviyoSubmitting = 'false';
      dispatch(form, 'loading', { isLoading: false });
    }
  };

  window.NhKlaviyoSubscribe = { subscribe };
  document.addEventListener('submit', submit);
})();
